import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  Alert,
  Button,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography
} from "@material-ui/core";
import { useModal as useMuiModal } from "mui-modal-provider";
import { ACCEPTED_FILE_TYPES, ListingFilePreviewCard } from "../../modal/core/ListingFilesUpload";
import { useTranslation } from "react-i18next";
import { Add, WhatsApp } from "@material-ui/icons";
import { ListingTableFilesShareDialog } from "./ListingTableFilesShareDialog";
import { useModal } from "hooks/useModal";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { setInitialFiles } from "utils/listings/new";
import { getFilesData, uploadFiles } from "utils/fileValidation";
import { textIncludes } from "utils/table";
import { db } from "firebaseConfig";
import { LoadingButton } from "@material-ui/lab";
import useAuth from "hooks/useAuth";
import { ModalAsyncResult, showModalAsync } from "components/__haim/Common/ConfirmationDialog";
import type { ListingWithAdditionalData } from "../../../../../@types/listings/new";
import type { FileOrFilePreview, FilePreview } from "../../../../../@types/listings/fileData";

type ListingTableFilesProps = {
  listing: ListingWithAdditionalData;
  isAuthorized: boolean;
};

export function ListingTableFiles({ listing, isAuthorized }: ListingTableFilesProps): JSX.Element {
  const { id, files } = listing;

  const {
    user: { email: userEmail }
  } = useAuth();

  const [inputSearch, setInputSearch] = useState("");

  const [filesPreview, setFilesPreview] = useState<FilePreview[]>(setInitialFiles(listing));
  const [selectedFiles, setSelectedFiles] = useState<FileOrFilePreview[]>(setInitialFiles(listing));

  const [loading, setLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const { open, openModal, closeModal } = useModal();

  const { t: translate } = useTranslation();

  const inputFileRef = useRef<HTMLInputElement>(null);

  const { showModal } = useMuiModal();

  useEffect(() => {
    if (!listing) return;

    setSelectedFiles(setInitialFiles(listing));
    setFilesPreview(setInitialFiles(listing));
  }, [listing]);

  const handleDownloadAll = async (): Promise<void> => {
    try {
      const zip = new JSZip();

      const downloadPromises: Promise<JSZip>[] = [];

      for (const { src, name } of files) {
        const promise = fetch(src)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error status: ${response.status}`);
            }

            return response.blob();
          })
          .then((blob) => zip.file(name, blob));

        downloadPromises.push(promise);
      }

      await Promise.all(downloadPromises);

      const content = await zip.generateAsync({ type: "blob" });

      saveAs(content, "listing_files.zip");
    } catch (err) {
      console.error("Error downloading files:", err);
    }
  };

  const handleFileUpload = async ({
    target: { files }
  }: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const filesData = getFilesData(files, filesPreview.length, 128);

    if (!filesData) {
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);

    const { selectedFilesData } = filesData;

    const newSelectedFiles = [...selectedFiles, ...selectedFilesData];

    try {
      const files = await uploadFiles(newSelectedFiles, userEmail);

      await db.collection("real-estate-listings").doc(id).update({
        files: files
      });
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFile = (targetId: string, withConfirmation?: boolean) => async (): Promise<void> => {
    if (withConfirmation) {
      const modalResult = await showModalAsync({
        showModalInstance: showModal,
        title: translate("listingsPage.deleteFile"),
        description: translate("listingsPage.deleteFileDescription"),
        showConfirm: true,
        showCancel: true
      });

      const modalIsConfirmed = modalResult === ModalAsyncResult.confirm;

      if (!modalIsConfirmed) return;
    }

    const newSelectedFiles = selectedFiles.filter(({ id }) => id !== targetId);

    setLoading(true);

    try {
      await db.collection("real-estate-listings").doc(id).update({
        files: newSelectedFiles
      });
    } catch (error) {
      console.error("Error removing file:", error);
    } finally {
      setLoading(false);
    }

    const { src } = filesPreview.find(({ id }) => id === targetId) ?? {};

    URL.revokeObjectURL(src);
  };

  const renameFile =
    (targetId: string) =>
    async (customName: string): Promise<void> => {
      setLoading(true);

      try {
        const newSelectedFiles = selectedFiles.map((f) => {
          if (f.id !== targetId) return f;

          return {
            ...f,
            nameOverride: customName
          };
        });

        await db.collection("real-estate-listings").doc(id).update({
          files: newSelectedFiles
        });
      } catch (error) {
        console.error("Error renaming file:", error);
      } finally {
        setLoading(false);
      }
    };

  const handleClickInput = (): void => inputFileRef.current?.click();

  const handleSnackbarClose = (): void => setSnackbarOpen(false);

  const handleSearchChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
    setInputSearch(value);
  };

  const filteredFilesPreview = filesPreview.filter(({ name, nameOverride }) => {
    if (!inputSearch) return true;

    const parsedName = nameOverride || name;

    return textIncludes(parsedName, inputSearch);
  });

  const isFilesExists = Boolean(filteredFilesPreview?.length);

  const isControlsDisabled = !isAuthorized || !isFilesExists;

  return (
    <Stack gap={2}>
      <Snackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: "100%" }}>
          {translate("listingsPage.warningFilesLimit")}
        </Alert>
      </Snackbar>
      <input
        style={{ display: "none" }}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        ref={inputFileRef}
        multiple
        onChange={handleFileUpload}
      />
      <ListingTableFilesShareDialog
        open={open}
        files={filteredFilesPreview}
        listing={listing}
        onClose={closeModal}
      />
      <Stack gap={2}>
        <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{translate("listingsPage.relatedFiles")}</Typography>
          <Stack direction="row">
            <IconButton color="success" onClick={openModal} disabled={isControlsDisabled}>
              <WhatsApp />
            </IconButton>
            <Button disabled={isControlsDisabled} onClick={handleDownloadAll}>
              {translate("listingsPage.control.downloadAll")}
            </Button>
          </Stack>
        </Stack>
        {!isAuthorized && (
          <Alert severity="warning">
            {translate("listingsPage.onlyOwnersCanViewAndDownloadFiles")}
          </Alert>
        )}
        <Stack
          direction={{
            xs: "column",
            sm: "row"
          }}
          gap={2}
          justifyContent="space-between"
          alignItems={{
            xs: "initial",
            sm: "center"
          }}
        >
          <LoadingButton
            variant="contained"
            loading={loading}
            startIcon={<Add />}
            onClick={handleClickInput}
          >
            {translate("listingsPage.uploadFiles")}
          </LoadingButton>
          <TextField
            value={inputSearch}
            onChange={handleSearchChange}
            size="small"
            placeholder={translate("listingsPage.filter.searchFiles")}
          />
        </Stack>
      </Stack>
      <Stack gap={2}>
        {isFilesExists ? (
          filteredFilesPreview.map((filePreview) => (
            <ListingFilePreviewCard
              file={filePreview}
              hidden={!isAuthorized}
              loading={loading}
              removeFile={removeFile(filePreview.id, true)}
              renameFile={renameFile(filePreview.id)}
              key={filePreview.id}
            />
          ))
        ) : (
          <Typography sx={{ mx: "auto" }} variant="subtitle1" color="textSecondary">
            {translate("listingsPage.error.noRelatedFilesFound")}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
